import React, { useState, useEffect } from "react";

// App-standard "no photo" look for a player/person: when there's no usable
// image (or it 404s) we render a GREY block with the person's initials —
// matching SearchTable / MyPlayers (bg-gray-100 + text-gray-500). This is the
// canonical fallback; NOT a running-man asset and NOT a remote placeholder
// (the old via.placeholder.com was dead → flicker). A local grey block can't
// fail, so there's no broken-image flash and no failed request.
//
// Props:
//   src        — photo url (null/empty → grey initials)
//   name       — used for the initials
//   className  — sizing/shape classes (e.g. "w-12 h-12 rounded-lg")
const getInitials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const PlayerAvatar = ({ src, name = "", alt, className = "" }) => {
  const [errored, setErrored] = useState(false);
  // Reset on src change so a new card with a valid photo isn't stuck on grey.
  useEffect(() => {
    setErrored(false);
  }, [src]);

  const showImage = src && !errored;

  if (showImage) {
    return (
      <img
        src={src}
        alt={alt || name}
        className={`object-cover ${className}`}
        // Swap to the grey initials block at most once — never a remote URL.
        onError={() => setErrored(true)}
      />
    );
  }

  return (
    <div
      className={`bg-gray-100 flex items-center justify-center text-gray-500 font-medium ${className}`}
      aria-label={alt || name || "No photo"}
    >
      {getInitials(name)}
    </div>
  );
};

export default PlayerAvatar;
